import { Toaster as Sonner, toast } from 'sonner';
import { useTheme } from './ThemeProvider';

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * The app's toaster, and the channel every error the user should see arrives on.
 *
 * It read its theme from `next-themes`, which nothing mounts - the app has its own
 * ThemeProvider - so `theme` was always undefined and every toast fell back to
 * "system". A dark app on a light OS therefore got light toasts. It follows the
 * app's own theme now.
 *
 * Surface is `panel`, not `background`: in the console `background` is the desk, the
 * darkest surface of the lot, so a toast drawn on it read as a hole rather than as
 * something floating above the page.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-panel group-[.toaster]:text-foreground group-[.toaster]:border-line group-[.toaster]:shadow-lg group-[.toaster]:rounded-card',
          description: 'group-[.toast]:text-dim',
          actionButton: 'group-[.toast]:bg-cta group-[.toast]:text-white',
          cancelButton: 'group-[.toast]:bg-field group-[.toast]:text-dim',
          error: 'group-[.toaster]:border-danger-line group-[.toaster]:text-danger',
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
